/**
 * <need-rx-order-item> Web Component
 * Migrated from src/alpine/components/need-rx-order-item.js
 *
 * Attributes:
 *   order-id        - Shopify order ID
 *   line-items-id   - JSON array of line item IDs, e.g. [123456]
 *   data-product-image - Product image URL (set by Liquid)
 *   data-order-number  - Order display name (set by Liquid)
 */
(function () {
  "use strict";

  var WAITING_TEXTS = [
    "We\u2019re reading your prescription",
    "Almost done processing",
    "Getting your prescription ready"
  ];

  class NeedRxOrderItem extends HTMLElement {
    constructor() {
      super();
      this._handlers = [];
      this._bound = false;
      this.state = this._defaultState();
    }

    _defaultState() {
      return {
        orderId: "",
        lineItemsId: [],
        lineItemId: null,
        isMultifocal: false,
        isRxNeeded: false,
        analyzing: false,
        loading: false,
        isShowRXForm: false,
        showRxModal: false,
        statusText: "",
        file: null,
        previewUrl: "",
        uploaded: false,
        requiredFields: [],
        isYearIssued: false,
        isYearExpired: false,
        waitingAnalyzeIndex: 0,
        waitingAnalyzeText: "",
        productImage: "",
        orderNumber: "",
        bundleProducts: [],
        bundleHash: "",
        manualFormField: {
          SPH: { R: "0.00", L: "0.00" },
          CYL: { R: "0.00", L: "0.00" },
          AXIS: { R: "000", L: "000" },
          PUPIL_RL: { RL: "None" },
          PUPIL: { R: "None", L: "None" },
          ADD: { R: "N/A", L: "N/A" },
          PRISM: {
            R: {
              vertical: { direction: "U", value: "0.00" },
              horizontal: { direction: "I", value: "0.00" }
            },
            L: {
              vertical: { direction: "U", value: "0.00" },
              horizontal: { direction: "I", value: "0.00" }
            }
          }
        },
        isOpenPD: false,
        isOpenPrism: false,
        prescriptionData: null,
        uniqueId: null,
        rxPreviewUrl: null
      };
    }

    /* ------------------------------------------------------------------ */
    /*  Lifecycle                                                          */
    /* ------------------------------------------------------------------ */

    connectedCallback() {
      this.state.orderId = this.getAttribute("order-id") || "";
      try {
        this.state.lineItemsId = JSON.parse(this.getAttribute("line-items-id") || "[]");
      } catch (e) {
        this.state.lineItemsId = [];
      }
      this.state.lineItemId = this.state.lineItemsId[0] || null;
      this.state.productImage = this.dataset.productImage || "";
      this.state.orderNumber = this.dataset.orderNumber || "";

      this._bindEvents();
      this.render();
      this._getOrders();
    }

    disconnectedCallback() {
      this._unbindEvents();
    }

    /* ------------------------------------------------------------------ */
    /*  DOM helpers                                                        */
    /* ------------------------------------------------------------------ */

    $ref(name) {
      return this.querySelector('[data-ref="' + name + '"]');
    }

    _getNestedValue(obj, path) {
      return path.split(".").reduce(function (o, k) { return o != null ? o[k] : undefined; }, obj);
    }

    _setNestedValue(obj, path, value) {
      var keys = path.split(".");
      var last = keys.pop();
      var target = keys.reduce(function (o, k) { return (o[k] = o[k] || {}); }, obj);
      target[last] = value;
    }

    /* ------------------------------------------------------------------ */
    /*  Rendering                                                          */
    /* ------------------------------------------------------------------ */

    render() {
      var s = this.state;

      /* — Text — */
      var el;
      el = this.$ref("orderNumber");
      if (el) el.textContent = s.orderNumber || "";

      el = this.$ref("statusBadge");
      if (el) {
        el.textContent = s.statusText || "";
        el.style.backgroundColor = s.isRxNeeded ? "#fff3e0" : "#f0f9f0";
        el.style.color = s.isRxNeeded ? "#e65100" : "#2e7d32";
      }

      el = this.$ref("waitingText");
      if (el) el.textContent = s.waitingAnalyzeText || "";

      el = this.$ref("drawerWaitingText");
      if (el) el.textContent = s.waitingAnalyzeText || "";

      /* — Images — */
      el = this.$ref("rxPreviewImg");
      if (el && s.rxPreviewUrl) el.src = s.rxPreviewUrl;

      el = this.$ref("rxModalImage");
      if (el && s.rxPreviewUrl) el.src = s.rxPreviewUrl;

      /* — Disabled — */
      el = this.$ref("manualEntryBtn");
      if (el) el.disabled = s.analyzing;

      el = this.$ref("submitBtn");
      if (el) el.disabled = s.loading || s.requiredFields.length > 0;

      /* — Drawer classes (CSS transition) — */
      el = this.$ref("drawer");
      if (el) el.classList.toggle("active", s.isShowRXForm);
      el = this.$ref("drawerOverlay");
      if (el) el.classList.toggle("active", s.isShowRXForm);

      /* — Visibility map — */
      var vis = {
        isRxNeeded:     s.isRxNeeded,
        rxCompleted:    !s.isRxNeeded && !s.analyzing,
        rxPreviewUrl:   !!s.rxPreviewUrl,
        rxPreviewActions: !!s.rxPreviewUrl,
        showRxModal:    s.showRxModal,
        analyzing:      s.analyzing,
        notAnalyzing:   !s.analyzing,
        isOpenPD:       s.isOpenPD,
        notIsOpenPD:    !s.isOpenPD,
        isOpenPrism:    s.isOpenPrism,
        loading:        s.loading,
        notLoading:     !s.loading,
        isYearExpired:  s.isYearExpired,
        isYearIssued:   s.isYearIssued
      };

      for (var key in vis) {
        var els = this.querySelectorAll('[data-show="' + key + '"]');
        var show = vis[key];
        for (var i = 0; i < els.length; i++) {
          els[i].style.display = show ? "" : "none";
        }
      }
    }

    /** Push state values into form inputs (after OCR / reset). */
    syncFormValues() {
      var self = this;
      this.querySelectorAll("[data-model]").forEach(function (input) {
        var val = self._getNestedValue(self.state, input.dataset.model);
        if (val === undefined) return;
        if (input.type === "checkbox") {
          input.checked = !!val;
        } else {
          input.value = val;
        }
      });
    }

    /* ------------------------------------------------------------------ */
    /*  Event binding                                                      */
    /* ------------------------------------------------------------------ */

    _bindEvents() {
      if (this._bound) return;
      this._bound = true;
      var self = this;

      /* data-action="[event:]method" */
      this.querySelectorAll("[data-action]").forEach(function (el) {
        var parts = el.dataset.action.split(":");
        var evtType = parts.length === 2 ? parts[0] : "click";
        var method  = parts.length === 2 ? parts[1] : parts[0];

        var handler = function (e) {
          if (evtType === "submit") e.preventDefault();
          if (typeof self[method] === "function") self[method](e);
        };

        el.addEventListener(evtType, handler);
        self._handlers.push({ el: el, evt: evtType, fn: handler });
      });

      /* data-model two-way binding */
      this.querySelectorAll("[data-model]").forEach(function (input) {
        var path = input.dataset.model;
        var evtType = input.type === "checkbox" ? "change" : "input";

        var handler = function () {
          var val = input.type === "checkbox" ? input.checked : input.value;
          self._setNestedValue(self.state, path, val);
          self._onFieldChange(path, val);
        };

        input.addEventListener(evtType, handler);
        self._handlers.push({ el: input, evt: evtType, fn: handler });
      });

      /* Modal overlay — close on background click */
      var overlay = this.$ref("rxModalOverlay");
      if (overlay) {
        var h = function (e) { if (e.target === overlay) self.closeRxModal(); };
        overlay.addEventListener("click", h);
        self._handlers.push({ el: overlay, evt: "click", fn: h });
      }

      /* Form submit */
      var form = this.$ref("rxForm");
      if (form) {
        var submitH = function (e) { e.preventDefault(); self._updatePrescription(); };
        form.addEventListener("submit", submitH);
        self._handlers.push({ el: form, evt: "submit", fn: submitH });
      }
    }

    _unbindEvents() {
      this._handlers.forEach(function (h) { h.el.removeEventListener(h.evt, h.fn); });
      this._handlers = [];
      this._bound = false;
    }

    /* ------------------------------------------------------------------ */
    /*  Field-change watchers (replaces Alpine $watch)                     */
    /* ------------------------------------------------------------------ */

    _onFieldChange(path, value) {
      var s = this.state;
      switch (path) {
        case "manualFormField.CYL.R":  this._watchCyl("R"); break;
        case "manualFormField.CYL.L":  this._watchCyl("L"); break;
        case "manualFormField.AXIS.R": this._watchAxis("R"); break;
        case "manualFormField.AXIS.L": this._watchAxis("L"); break;
        case "manualFormField.ADD.R":  this._watchAdd("R"); break;
        case "manualFormField.ADD.L":  this._watchAdd("L"); break;
        case "manualFormField.PUPIL_RL.RL":
          this._updateRequired("PUPIL_RL.RL", !s.isOpenPD && value === "None"); break;
        case "manualFormField.PUPIL.R":
          this._updateRequired("PUPIL.R", s.isOpenPD && value === "None"); break;
        case "manualFormField.PUPIL.L":
          this._updateRequired("PUPIL.L", s.isOpenPD && value === "None"); break;
      }
      this.render();
    }

    _watchCyl(eye) {
      this._updateRequired("AXIS." + eye, Number(this.state.manualFormField.CYL[eye]) !== 0);
    }
    _watchAxis(eye) {
      if (this.state.manualFormField.AXIS[eye] !== "000") this._updateRequired("AXIS." + eye, false);
    }
    _watchAdd(eye) {
      if (this.state.isMultifocal && this.state.manualFormField.ADD[eye] !== "N/A")
        this._updateRequired("ADD." + eye, false);
    }

    _updateRequired(field, isRequired) {
      var idx = this.state.requiredFields.indexOf(field);
      if (isRequired && idx === -1) this.state.requiredFields.push(field);
      else if (!isRequired && idx !== -1) this.state.requiredFields.splice(idx, 1);
    }

    /* ------------------------------------------------------------------ */
    /*  API / Business logic                                               */
    /* ------------------------------------------------------------------ */

    async _getOrders() {
      try {
        var data = await MO.proxyApi.getOrder(this.state.orderId);
        if (!data || !data.success) { console.warn("[need-rx] getOrder failed:", this.state.orderId); return; }

        var order = data.order || (data.data && data.data.order);
        if (!order) return;

        var lineItems = order.line_items || [];

        /* Find RX line item */
        var rxLineItem = lineItems.find(function (item) {
          return (item.properties || []).some(function (p) { return p.name === "rxOrder" && p.value === "true"; });
        });

        if (rxLineItem) {
          var bhProp = (rxLineItem.properties || []).find(function (p) { return p.name === "_bundleHash"; });
          this.state.bundleHash = bhProp ? bhProp.value : "";

          if (this.state.bundleHash) {
            var bh = this.state.bundleHash;
            this.state.bundleProducts = lineItems.filter(function (item) {
              var p = (item.properties || []).find(function (pp) { return pp.name === "_bundleHash"; });
              return p && p.value === bh;
            }).map(function (item) {
              return { id: item.id, name: item.name || item.title, sku: item.sku };
            });
          }
        }

        this.state.orderNumber = order.name || ("#" + order.order_number) || String(order.id);

        /* Extract uniqueId */
        var uniqueId;
        lineItems.forEach(function (item) {
          (item.properties || []).forEach(function (p) { if (p.name === "uniqueId") uniqueId = p.value; });
        });
        if (uniqueId) this._getFile(uniqueId);

        /* Status */
        var labStatus = order.labStatus || (order.labStatusInfo && order.labStatusInfo.labStatus) || "rx_needed";
        var statusMap = { rx_needed: "RX Needed", sent: "Sent to lab", in_progress: "Manufacturing", completed: "Shipping", cancelled: "Cancelled" };
        this.state.statusText = statusMap[labStatus] || labStatus;
        this.state.isRxNeeded = labStatus === "rx_needed";
        this.render();
      } catch (err) {
        console.error("[need-rx] getOrders error:", err);
      }
    }

    async _getFile(uniqueId) {
      if (!uniqueId) return;
      try {
        var d = await MO.proxyApi.getPrescriptionFile(uniqueId);
        this.state.rxPreviewUrl = (d && d.fileUrl) || (d && d.data && d.data.fileUrl) || null;
        this.render();
      } catch (e) { console.error("[need-rx] getFile error:", e); }
    }

    async _analyzePrescription() {
      if (!this.state.file) return false;

      this.open();
      this.state.analyzing = true;
      this.state.waitingAnalyzeIndex = 0;
      this.state.waitingAnalyzeText = WAITING_TEXTS[0];
      this.render();

      var self = this;
      var intervalId = setInterval(function () {
        var idx = self.state.waitingAnalyzeIndex + 1;
        if (idx < WAITING_TEXTS.length) {
          self.state.waitingAnalyzeIndex = idx;
          self.state.waitingAnalyzeText = WAITING_TEXTS[idx];
          self.render();
        }
      }, 3000);

      try {
        var minWait = new Promise(function (r) { setTimeout(r, 6000); });
        var results = await Promise.all([MO.proxyApi.analyzePrescription(this.state.file), minWait]);
        var result = results[0];
        if (!result || typeof result !== "object") throw new Error("Invalid response");
        return result;
      } catch (err) {
        console.error("[need-rx] analyzePrescription error:", err);
        return false;
      } finally {
        clearInterval(intervalId);
        this.state.analyzing = false;
        this.render();
      }
    }

    async _prepareData() {
      var year = new Date().getFullYear();
      this.state.isYearExpired = false;
      this.state.isYearIssued = false;

      var data = await this._analyzePrescription();
      if (!data) return;

      var analysis = data.analysis;
      var pd = data.prescriptionData;
      var uniqueId = data.uniqueId;

      if (analysis.consensus.yearExpired && year > analysis.consensus.yearExpired) {
        this.state.isYearExpired = true; this.state.file = null; this.state.uploaded = false;
        this.render(); return;
      }
      if (analysis.consensus.yearIssued && year - analysis.consensus.yearIssued > 2) {
        this.state.isYearIssued = true; this.state.file = null; this.state.uploaded = false;
        this.render(); return;
      }

      this.state.isOpenPD = pd.isMonocularPd;
      this.state.uniqueId = uniqueId;

      var f = this.state.manualFormField;
      if (this.state.isOpenPD) {
        f.PUPIL.R = (pd.pdRight != null && pd.pdRight !== "") ? pd.pdRight : "None";
        f.PUPIL.L = (pd.pdLeft  != null && pd.pdLeft  !== "") ? pd.pdLeft  : "None";
      } else {
        var pdVal = pd.pupillaryDistance;
        f.PUPIL_RL.RL = (pdVal != null && pdVal !== "" && Number.isFinite(Number(pdVal))) ? pdVal : "None";
      }

      var map = [
        { field: "SPH",  keys: ["sphRight",  "sphLeft" ] },
        { field: "CYL",  keys: ["cylRight",  "cylLeft" ] },
        { field: "AXIS", keys: ["axisRight", "axisLeft"] },
        { field: "ADD",  keys: ["addRight",  "addLeft" ] }
      ];
      var self = this;
      map.forEach(function (m) {
        ["R", "L"].forEach(function (eye, i) {
          var v = pd[m.keys[i]];
          if (v != null && v !== "" && Number.isFinite(Number(v))) f[m.field][eye] = v;
        });
      });

      this._extractPrismFromAnalysis(analysis);
      this._watchAdd("L");
      this._watchAdd("R");
      this.syncFormValues();
      this.render();
    }

    async _updatePrescription() {
      var s = this.state;
      if (!s.lineItemId) { console.error("[need-rx] Missing lineItemId"); return; }

      s.loading = true;
      this.render();

      try {
        var payload = this._buildPayload();
        var result = await MO.proxyApi.savePrescription({
          orderId: s.orderId,
          lineItemId: s.lineItemId,
          prescriptionData: payload
        });

        if (!result.success) throw new Error("Save failed");

        var rxUID = result.rxUID || (result.data && result.data.rxUID);
        await MO.proxyApi.updateLineItemProperties({
          orderId: String(s.orderId),
          lineItemId: String(s.lineItemId),
          properties: {
            "Prescription RX UID": rxUID,
            uniqueId: s.uniqueId,
            rx_method: s.uniqueId ? "file" : "manual"
          }
        });

        this.close();
        s.file = null; s.uploaded = false; s.previewUrl = "";
        s.isRxNeeded = false;
        this._resetForm();
        this.render();
      } catch (err) {
        console.error("[need-rx] updatePrescription error:", err);
      } finally {
        s.loading = false;
        this.render();
      }
    }

    /* ------------------------------------------------------------------ */
    /*  PRISM helpers                                                       */
    /* ------------------------------------------------------------------ */

    _baseToWord(d)   { return { O: "Out", I: "In", U: "Up", D: "Down" }[d] || d; }
    _abbrToWord(a)   { return { BO: "Out", BI: "In", BU: "Up", BD: "Down" }[a] || a; }
    _wordToLetter(w) { return { Out: "O", In: "I", Up: "U", Down: "D" }[w] || (w ? w.charAt(0).toUpperCase() : ""); }

    _extractPrismFromAnalysis(analysis) {
      if (!analysis || !analysis.consensus || !analysis.consensus.prescriptionDetails) {
        this.state.isOpenPrism = false; return;
      }
      var det = analysis.consensus.prescriptionDetails;
      var R = det.rightEye || {};
      var L = det.leftEye  || {};
      if (!R.prismHorizontal && !R.prismVertical && !L.prismHorizontal && !L.prismVertical) {
        this.state.isOpenPrism = false; return;
      }
      this.state.isOpenPrism = true;
      var f = this.state.manualFormField.PRISM;
      var self = this;

      function setPrism(eye, axis, raw, baseRaw) {
        if (!raw) return;
        f[eye][axis].value = parseFloat(raw).toFixed(2);
        var letter = self._wordToLetter(self._abbrToWord(baseRaw));
        if (letter) f[eye][axis].direction = letter;
      }
      setPrism("R", "horizontal", R.prismHorizontal, R.baseHorizontal);
      setPrism("R", "vertical",   R.prismVertical,   R.baseVertical);
      setPrism("L", "horizontal", L.prismHorizontal, L.baseHorizontal);
      setPrism("L", "vertical",   L.prismVertical,   L.baseVertical);
    }

    /* ------------------------------------------------------------------ */
    /*  Payload builder                                                    */
    /* ------------------------------------------------------------------ */

    _buildPayload() {
      var f = this.state.manualFormField;
      var pd = {
        sphRight:  f.SPH.R,  cylRight:  f.CYL.R,  axisRight: f.AXIS.R,
        addRight:  f.ADD.R === "N/A" ? "0.00" : f.ADD.R,
        sphLeft:   f.SPH.L,  cylLeft:   f.CYL.L,  axisLeft:  f.AXIS.L,
        addLeft:   f.ADD.L === "N/A" ? "0.00" : f.ADD.L,
        isMonocularPd: this.state.isOpenPD
      };

      if (this.state.isOpenPrism) {
        pd.isPrism = true;
        pd.Service = "FOCM";
        pd.prismFormat = "directional";
        var bw = this._baseToWord.bind(this);
        pd.prismRight = {
          vertical:   { value: f.PRISM.R.vertical.value,   base: bw(f.PRISM.R.vertical.direction) },
          horizontal: { value: f.PRISM.R.horizontal.value, base: bw(f.PRISM.R.horizontal.direction) }
        };
        pd.prismLeft = {
          vertical:   { value: f.PRISM.L.vertical.value,   base: bw(f.PRISM.L.vertical.direction) },
          horizontal: { value: f.PRISM.L.horizontal.value, base: bw(f.PRISM.L.horizontal.direction) }
        };
      }

      if (this.state.isOpenPD) {
        pd.pdLeft  = parseFloat(f.PUPIL.L) + "mm";
        pd.pdRight = parseFloat(f.PUPIL.R) + "mm";
      } else {
        pd.pdValue = f.PUPIL_RL.RL + "mm";
      }

      return { data: { prescriptionData: pd } };
    }

    /* ------------------------------------------------------------------ */
    /*  UI actions (referenced by data-action in Liquid)                   */
    /* ------------------------------------------------------------------ */

    handleFileInput(e) {
      var file = e.target.files && e.target.files[0];
      if (!file) return;
      this.state.file = file;
      this.state.uploaded = true;
      this.state.previewUrl = URL.createObjectURL(file);
      e.target.value = "";
      this.render();
      this._prepareData();
    }

    open() {
      this.state.isShowRXForm = true;
      MO.scrollLock.enable();
      this.render();
    }

    close() {
      if (this.state.analyzing) return;
      this.state.isShowRXForm = false;
      MO.scrollLock.disable();
      this.render();
    }

    openRxModal() {
      this.state.showRxModal = true;
      document.body.style.overflow = "hidden";
      this.render();
    }

    closeRxModal() {
      this.state.showRxModal = false;
      document.body.style.overflow = "";
      this.render();
    }

    togglePDOption(e) {
      /* Read from checkbox if triggered by event, otherwise just toggle */
      this.state.isOpenPD = e && e.target && e.target.type === "checkbox" ? e.target.checked : !this.state.isOpenPD;
      this.state.manualFormField.PUPIL.L = "None";
      this.state.manualFormField.PUPIL.R = "None";
      this.state.manualFormField.PUPIL_RL.RL = "None";
      this.syncFormValues();
      this.render();
    }

    togglePrismBlock(e) {
      this.state.isOpenPrism = e && e.target && e.target.type === "checkbox" ? e.target.checked : !this.state.isOpenPrism;
      this.render();
    }

    openIframePD() {
      document.documentElement.insertAdjacentHTML("beforeend",
        '<div id="pd-iframe" style="position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(6px);background:rgba(255,255,255,.9)">' +
          '<div style="position:relative;width:75vw;height:75vh;overflow:hidden;border-radius:32px">' +
            '<iframe style="width:100%;height:100%;border:none" src="https://pd.eazyvisi.com/?primaryColor=FFF&textColor=000&lineColor=FFF&borderRadius=3px" allow="camera"></iframe>' +
          '</div>' +
        '</div>'
      );
      var el = document.getElementById("pd-iframe");
      if (el) el.addEventListener("click", function (ev) { if (ev.target === el) el.remove(); });
    }

    _resetForm() {
      var f = this.state.manualFormField;
      f.SPH.R = "0.00"; f.SPH.L = "0.00";
      f.CYL.R = "0.00"; f.CYL.L = "0.00";
      f.AXIS.R = "000";  f.AXIS.L = "000";
      f.PUPIL_RL.RL = "None";
      f.PUPIL.R = "None"; f.PUPIL.L = "None";
      f.ADD.R = "N/A"; f.ADD.L = "N/A";
      f.PRISM.R.vertical   = { direction: "U", value: "0.00" };
      f.PRISM.R.horizontal = { direction: "I", value: "0.00" };
      f.PRISM.L.vertical   = { direction: "U", value: "0.00" };
      f.PRISM.L.horizontal = { direction: "I", value: "0.00" };
      this.state.isOpenPD = false;
      this.state.isOpenPrism = false;
      this.state.requiredFields = [];
      this.syncFormValues();
    }
  }

  customElements.define("need-rx-order-item", NeedRxOrderItem);
})();
